export type TonoDeEstado =
  | 'borrador'
  | 'espera'
  | 'aprobada'
  | 'reapertura'
  | 'ajuste'
  | 'rechazada'
  | 'vencida';

const ETIQUETAS: Record<string, { texto: string; tono: TonoDeEstado }> = {
  DRAFT: { texto: 'Borrador', tono: 'borrador' },
  PENDING_APPROVAL: { texto: 'En aprobación', tono: 'espera' },
  APPROVED: { texto: 'Aprobada', tono: 'aprobada' },
  ADJUSTMENT_REQUESTED: { texto: 'Ajuste solicitado', tono: 'ajuste' },
  REJECTED: { texto: 'Rechazada', tono: 'rechazada' },
  EXPIRED: { texto: 'Vencida', tono: 'vencida' },
  CANCELLED: { texto: 'Cancelada', tono: 'borrador' },
  FROZEN: { texto: 'Congelada', tono: 'borrador' },
};

/**
 * El estado en español. Nunca el código crudo.
 * `retirada`: «Ajuste solicitado» porque quien la creó la retiró para corregir (D9, 17-oct): dice «Retirada», igual que
 * el banner «Retirada por … para corregir». `reabierta` es lo mismo para una reapertura aceptada. En la ficha sale de `retiradaParaCorregir`; en la lista, del servidor.
 */
export function etiquetaDeEstado(
  status: string,
  reaperturaPendiente: boolean,
  retirada = false,
  reabierta = false
): { texto: string; tono: TonoDeEstado } {
  if (status === 'APPROVED' && reaperturaPendiente) {
    return { texto: 'Reapertura solicitada', tono: 'reapertura' };
  }
  if (status === 'ADJUSTMENT_REQUESTED' && retirada) {
    return { texto: 'Retirada', tono: 'ajuste' };
  }
  // Recorrido B (17-oct): una reapertura aceptada deja la cotización en «Ajuste solicitado», pero el banner dice
  // «Reabierta para ajuste»: la pastilla dice lo mismo.
  if (status === 'ADJUSTMENT_REQUESTED' && reabierta) {
    return { texto: 'Reabierta para ajuste', tono: 'ajuste' };
  }
  return ETIQUETAS[status] ?? { texto: 'Sin estado', tono: 'borrador' };
}

export function reaperturaPendiente(
  approvals: Array<{ tipo?: string; status: string }> | undefined
): boolean {
  return (approvals ?? []).some((a) => a.tipo === 'REAPERTURA' && a.status === 'PENDING');
}

/** Colores del chip de estado: los mismos en la lista del Pricer, la ficha y la lista general de Cotizaciones. */
export const COLORES_DE_ESTADO: Record<string, { bg: string; color: string }> = {
  DRAFT: { bg: 'hsl(var(--muted))', color: 'hsl(var(--foreground) / 0.8)' },
  PENDING_APPROVAL: { bg: 'hsl(var(--alerta-suave))', color: 'hsl(var(--alerta))' },
  APPROVED: { bg: 'hsl(var(--ok-suave))', color: 'hsl(var(--ok))' },
  REJECTED: { bg: 'hsl(var(--bloqueo-suave))', color: 'hsl(var(--bloqueo))' },
  ADJUSTMENT_REQUESTED: { bg: 'hsl(var(--alerta-suave))', color: 'hsl(var(--alerta))' },
  CANCELLED: { bg: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' },
  EXPIRED: { bg: 'hsl(var(--alerta-suave))', color: 'hsl(var(--alerta))' },
  FROZEN: { bg: 'hsl(var(--info-suave))', color: 'hsl(var(--primary))' },
};
